import { type } from 'arktype';
import { journalMonthSchema } from './person-journal.model';

/** What blocks the month close for one person+day (story F7-1, AC1). */
export const monthCloseBlockerKinds = ['review-status', 'verdict', 'missing-context'] as const;
export type MonthCloseBlockerKind = (typeof monthCloseBlockerKinds)[number];

/**
 * One gate blocker: a person+day that keeps the month open. `detail` carries
 * the review status (`proposed`/`needs_review`/`rejected`) for kind
 * `review-status`, otherwise the rule kind key or required-context key.
 */
export interface MonthCloseBlocker {
  personId: string;
  personDisplayName: string;
  /** Day key (`YYYY-MM-DD`, see `dayKeySchema`). */
  bookingDate: string;
  kind: MonthCloseBlockerKind;
  detail: string;
  /** Route to the queue or person-day (e.g. `/time-tracking/review-queue?...`). */
  deepLink: string;
}

/** Warning source carried into the close protocol without blocking (AC1: warn verdicts). */
export const monthCloseWarningKinds = ['warn-verdict'] as const;
export type MonthCloseWarningKind = (typeof monthCloseWarningKinds)[number];

/**
 * One gate warning: documented into the close protocol, never blocking.
 * `detail` carries the rule kind key; `amount` is a preformatted display
 * string (e.g. `+42 h`) when the warning has a magnitude.
 */
export interface MonthCloseWarning {
  personId: string;
  personDisplayName: string;
  kind: MonthCloseWarningKind;
  detail: string;
  amount?: string;
}

/** Readiness strip keys (gate-only slice: no payroll item — F7-3 owns it). */
export const monthCloseReadinessKeys = ['precheck', 'queue-empty', 'violations-decided'] as const;
export type MonthCloseReadinessKey = (typeof monthCloseReadinessKeys)[number];

/** One readiness-strip entry; `label` is an i18n key. */
export interface MonthCloseReadiness {
  key: MonthCloseReadinessKey;
  label: string;
  ok: boolean;
}

/** Lifecycle of a close run: `closed` until an F6-1 unlock reopens it. */
export const monthCloseRunStatuses = ['closed', 'reopened'] as const;
export type MonthCloseRunStatus = (typeof monthCloseRunStatuses)[number];

/** Latest close run at a glance, embedded in the gate status. */
export interface MonthCloseRunSummary {
  id: string;
  version: number;
  status: MonthCloseRunStatus;
  /** PersonId of the HR user who closed. */
  closedBy: string;
  /** Display name for closedBy; UI renders this, never the UUID. */
  closedByDisplayName: string;
  closedAt: string;
  /** Display name of reopener; null while never reopened. */
  reopenedByDisplayName: string | null;
  /** Reopen instant; null while never reopened. */
  reopenedAt: string | null;
  /** Reopen reason; null while never reopened. */
  reopenReason: string | null;
}

/**
 * Response of `GET /time-tracking/month-close/:month/status` (AC1): blockers
 * (must be empty to close), warnings (carried along), stats trio, readiness
 * strip, and the latest run when the month is closed.
 */
export interface MonthCloseGateStatus {
  /** Closed month (`YYYY-MM`). */
  month: string;
  isClosed: boolean;
  blockers: MonthCloseBlocker[];
  warnings: MonthCloseWarning[];
  /** Stats trio of the gate hero: bookings, open violations, warnings. */
  counts: {
    bookings: number;
    openViolations: number;
    warnings: number;
  };
  readiness: MonthCloseReadiness[];
  /** Latest run summary; null while the month was never closed. */
  closeRun: MonthCloseRunSummary | null;
}

/**
 * Body of `POST /time-tracking/month-close/:month/close` (AC2). `confirm` is
 * the Umfangs-Confirm — closing without it is a 400; closing with open
 * blockers is a 409.
 */
export const monthCloseSchema = type({
  month: journalMonthSchema,
  confirm: 'true',
});
export type MonthCloseInput = typeof monthCloseSchema.t;

/** Result of a successful close (AC2). */
export interface MonthCloseResult {
  runId: string;
  month: string;
  version: number;
  /** UTC instant the period lock + snapshots were written. */
  lockedAt: string;
  /** Number of warnings snapshotted into the protocol. */
  warningsCarried: number;
  /** PersonId of the HR user who closed. */
  closedBy: string;
  /** Display name for closedBy; UI renders this, never the UUID. */
  closedByDisplayName: string;
}

/**
 * Body of the F6-1 unlock-flow reopen (AC3). Four-eyes + reason enforced
 * server-side; the reason lands on the run protocol.
 */
export const monthCloseReopenSchema = type({
  month: journalMonthSchema,
  reason: '1 <= string <= 1000',
});
export type MonthCloseReopenInput = typeof monthCloseReopenSchema.t;

/** Result of a reopen; re-close creates a new run version (AC3). */
export interface MonthCloseReopenResult {
  runId: string;
  reopenedAt: string;
}

/**
 * One close-run protocol entry (AC2/AC3, append-only): who closed when,
 * which warnings were carried, and the reopen trail when unlocked. Old runs
 * stay untouched when a re-close creates a new version.
 */
export interface MonthCloseRun {
  id: string;
  month: string;
  version: number;
  status: MonthCloseRunStatus;
  closedBy: string;
  /** Display name for closedBy; UI renders this, never the UUID. */
  closedByDisplayName: string;
  closedAt: string;
  reopenedBy?: string;
  /** Display name for reopenedBy; set when reopenedBy is set. */
  reopenedByDisplayName?: string;
  reopenedAt?: string;
  reopenReason?: string;
  /** Warning snapshot frozen at close time. */
  warningsCarried: MonthCloseWarning[];
}
